import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from "@sentry/react"
import App from './App.tsx'
import './index.css'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
type StandaloneNavigator = Navigator & { standalone?: boolean }

const isStandaloneDisplayMode = () => {
  if (typeof window === 'undefined') return false

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean((window.navigator as StandaloneNavigator).standalone)
  )
}

const syncAppViewport = () => {
  if (typeof window === 'undefined') return

  const viewportHeight = window.visualViewport?.height ?? window.innerHeight
  document.documentElement.style.setProperty('--app-height', `${viewportHeight}px`)
  document.documentElement.classList.toggle('pwa-standalone', isStandaloneDisplayMode())
}

const scheduleViewportSync = () => {
  if (typeof window === 'undefined') return

  syncAppViewport()
  window.requestAnimationFrame(() => {
    syncAppViewport()
    window.requestAnimationFrame(syncAppViewport)
  })

  window.setTimeout(syncAppViewport, 50)
  window.setTimeout(syncAppViewport, 200)
  window.setTimeout(syncAppViewport, 500)
}

if (sentryDsn) {
  const isProd = import.meta.env.PROD
  Sentry.init({
    dsn: sentryDsn,
    // Structured logs to Sentry are noisy; keep off in prod to stay under quota.
    enableLogs: !isProd,
    environment: isProd ? 'production' : 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
      // Forwarding console to Sentry multiplies envelope volume; dev only.
      ...(isProd
        ? []
        : [Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] })]),
    ],
    tracesSampleRate: isProd ? 0.05 : 1.0,
    replaysSessionSampleRate: isProd ? 0.05 : 0.5,
    replaysOnErrorSampleRate: isProd ? 0.5 : 1.0,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      if (event.exception) {
        const error = hint.originalException;
        if (error instanceof Error) {
          if (error.message?.includes('Network') || error.message?.includes('abort')) {
            return null;
          }
        }
      }
      return event;
    }
  })
  
  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason);
  });
}

// Sync standalone state and real viewport height for iOS Safari/PWA differences.
if (typeof window !== 'undefined') {
  scheduleViewportSync()
  window.addEventListener('load', scheduleViewportSync)
  window.addEventListener('pageshow', scheduleViewportSync)
  window.addEventListener('focus', scheduleViewportSync)
  window.addEventListener('resize', scheduleViewportSync)
  window.addEventListener('orientationchange', scheduleViewportSync)
  window.addEventListener('touchstart', scheduleViewportSync, { passive: true })
  window.addEventListener('touchend', scheduleViewportSync, { passive: true })
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      scheduleViewportSync()
    }
  })
  window.visualViewport?.addEventListener('resize', scheduleViewportSync)
  window.visualViewport?.addEventListener('scroll', scheduleViewportSync)
}

// Register service worker via Vite PWA plugin (enables update detection)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  import('virtual:pwa-register').then(({ registerSW }) => {
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // New content available; expose updater so UI can apply it explicitly.
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(
            new CustomEvent('pwa-update-available', {
              detail: {
                applyUpdate: () => updateSW(),
              },
            })
          );
        }
      },
      onOfflineReady() {
        console.log('PWA ready for offline use');
      },
    });
  }).catch(() => {
    // Fallback if virtual module not available (e.g. build without PWA)
    navigator.serviceWorker?.register('/sw.js').catch(() => {});
  });
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
) 